import React from "react";

import { listFormat } from "~/lib/utils";
import useSplitUsersIntoOthers from "~/ui/avatar/useSplitUsersIntoOthers";
import Tooltip from "~/ui/Tooltip";

import { OthersList } from "./AvatarList";

export type NameListUser = {
	id: string;
	fullName: string;
	firstName?: string;
	image?: string;
};

interface SingleNameProps {
	user: NameListUser;
	/** The ID of the user to refer to as "yourself". */
	selfId?: string;
	/** Refers to user as "yourself" rather than "you", for when they're the subject and object of a verb.
	 * Make sure to provide the correct `selfId` if this is `true`. */
	reflexive?: boolean;
	capitalize?: boolean;
	/** Whether to use the self user's name. Make sure to provide the correct `selfId` if this is `true`. */
	useSelfName?: boolean;
	useFirstName?: boolean;
}

// Render a single user, possibly using a pronoun
export function SingleName(props: SingleNameProps) {
	const { user, useSelfName, reflexive, capitalize, useFirstName, selfId } = props;

	if (selfId && user.id === selfId && !useSelfName) {
		if (reflexive) {
			return <React.Fragment>{capitalize ? "Yourself" : "yourself"}</React.Fragment>;
		} else {
			return <React.Fragment>{capitalize ? "You" : "you"}</React.Fragment>;
		}
	}

	return <>{useFirstName && user.firstName ? user.firstName : user.fullName}</>;
}

type NameListProps = Omit<SingleNameProps, "user"> & {
	users: readonly NameListUser[];
	/** Whether to omit the self user from the list. Make sure to provide the correct `selfId` if this is `true`. */
	omitSelf?: boolean;
	/** Maximum number of users to show (including the “+N” avatar or “and N others” text as one) */
	maxLength?: number;
	hideOtherNamesTooltip?: boolean;
};

export default function NameList({
	users,
	maxLength = 1,
	omitSelf = false,
	capitalize,
	hideOtherNamesTooltip = false,
	selfId = "",
	...singleNameProps
}: NameListProps) {
	const { usersToShow, hiddenUsers } = useSplitUsersIntoOthers<NameListUser>(users, selfId, maxLength, omitSelf);

	let items = usersToShow.map((user, index) => {
		return (
			<SingleName
				key={user.id}
				user={user}
				capitalize={index === 0 && capitalize}
				selfId={selfId}
				{...singleNameProps}
			/>
		);
	});

	if (hiddenUsers.length > 0) {
		items = items.concat(
			<OtherNames
				users={hiddenUsers}
				shownUsersCount={usersToShow.length}
				hideOtherNamesTooltip={hideOtherNamesTooltip}
				selfId={selfId}
				{...singleNameProps}
			/>,
		);
	}

	return <React.Fragment>{listFormat(items)}</React.Fragment>;
}

function OtherNames({
	users,
	shownUsersCount,
	hideOtherNamesTooltip,
	...singleProps
}: Omit<NameListProps, "omitSelf" | "maxLength" | "capitalize"> & {
	shownUsersCount: number;
	selfId: string;
}) {
	if (users.length === 1) {
		return <SingleName user={users[0]} {...singleProps} />;
	}

	return (
		<Tooltip
			content={<OthersList users={users} selfId={singleProps.selfId} />}
			interactive
			disabled={hideOtherNamesTooltip}
			delay={[250, 0]}
			placement="bottom"
		>
			<span>
				{shownUsersCount === 0 ? (
					// i.e. Share this meeting with 5 people
					<>
						{users.length}&nbsp;{users.length > 1 ? "people" : "person"}
					</>
				) : (
					// i.e. Share this meeting with Ray, Tolu, and 5 others
					<>
						{users.length}&nbsp;other{users.length > 1 ? "s" : ""}
					</>
				)}
			</span>
		</Tooltip>
	);
}
